import { useState } from "react";
import {
  Card,
  Button,
  Icon,
  Notice,
  Tabs,
  TabBar,
  Tab,
  TabPanel,
} from "@conuti-das/prince-ui";
import { Flex, Stack, Spinner, princeIcon } from "@/ui-prince";
import { validatorApi, type ValidationResult as VR } from "@/api/validatorApi";
import { BundleSelector } from "./BundleSelector";
import { EdiJsonView } from "./EdiJsonView";
import { EdifactHighlight } from "./EdifactHighlight";
import { EdifactInput } from "./EdifactInput";
import { ValidationResult } from "./ValidationResult";
import { ValidatorSubNav } from "./ValidatorSubNav";

export function ValidatorPage() {
  const [bundleId, setBundleId] = useState("");
  const [raw, setRaw] = useState("");
  const [pruefid, setPruefid] = useState("");
  const [result, setResult] = useState<VR | null>(null);
  const [ediJson, setEdiJson] = useState<unknown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("validation");

  async function handleValidate() {
    setError(null);
    setResult(null);
    setEdiJson(null);
    setBusy(true);
    setActiveTab("validation");
    try {
      // convert-and-validate liefert in einem Roundtrip sowohl die gemappte
      // EDI-JSON-Repräsentation (MIG/AHB-angereichert) als auch das
      // Validation-Result -- gleicher Aufwand wie /validate, aber ein
      // zusätzlicher Drill-Down im UI.
      const r = await validatorApi.convertAndValidate(raw, bundleId, pruefid || undefined);
      setResult(r.validation);
      setEdiJson(r.convert);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2 className="page-title">EDIFACT Validator</h2>
        <div className="page-subtitle">
          Validiert EDIFACT-Nachrichten gegen ein MIG/AHB-Bundle aus
          maco-validation-bundles. Zeigt fehlende/übersprungene Checker-Logik
          pro Nachrichtentyp.
        </div>
      </div>

      <ValidatorSubNav />

      <Card>
        <Stack style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-3)" }}>
          <Flex style={{ gap: "var(--s-4)", flexWrap: "wrap", alignItems: "center" }}>
            <BundleSelector value={bundleId} onChange={setBundleId} />
            <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
              <label htmlFor="pruefid-input" className="prn-field-label">
                Prüfid (optional)
              </label>
              <input
                id="pruefid-input"
                className="prn-input"
                value={pruefid}
                onChange={(e) => setPruefid(e.target.value ?? "")}
                placeholder="z.B. 55001"
                style={{ width: "120px" }}
              />
            </Flex>
          </Flex>
        </Stack>
      </Card>

      <Card style={{ marginTop: "var(--s-3)" }}>
        <Stack style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-3)" }}>
          <EdifactInput value={raw} onChange={setRaw} />
          <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
            <Button
              variant="filled"
              isDisabled={!raw.trim() || !bundleId || busy}
              onPress={() => void handleValidate()}
            >
              <Icon name={princeIcon("validate")} size={16} />
              {busy ? "Validiere ..." : "Validieren"}
            </Button>
            {busy && <Spinner size="s" />}
            <span style={{ color: "var(--sapContent_LabelColor)", fontSize: "0.8rem" }}>
              Service: <code>/api/validator</code>
            </span>
          </Flex>
        </Stack>
      </Card>

      {error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="negative">
            Fehler bei der Validierung: {error}
          </Notice>
        </div>
      )}

      {/* Result-Tabs: Validation + EDI-JSON nebeneinander. Erst nach erstem
          erfolgreichem Validate sichtbar. */}
      {result && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => {
              if (key != null) setActiveTab(String(key));
            }}
          >
            <TabBar aria-label="Validierungsergebnis">
              <Tab id="validation">
                {result.valid ? "Validation · gültig" : "Validation · ungültig"}
                {" "}
                <span style={{ fontSize: "0.78rem", opacity: 0.7 }}>
                  {result.valid
                    ? `${result.statistics.segments_processed} Segmente`
                    : `${result.statistics.errors} Fehler · ${result.statistics.warnings} Warnungen`}
                </span>
              </Tab>
              <Tab id="edijson" isDisabled={ediJson === null}>
                EDI-JSON{ediJson ? " · gemappt" : ""}
              </Tab>
            </TabBar>
            <TabPanel id="validation">
              <div style={{ padding: "var(--s-3) var(--s-4)" }}>
                <ValidationResult
                  result={result}
                  rawEdi={raw}
                  HighlightComponent={EdifactHighlight}
                />
              </div>
            </TabPanel>
            <TabPanel id="edijson">
              <div style={{ padding: "var(--s-3) var(--s-4)" }}>
                <EdiJsonView json={ediJson} />
              </div>
            </TabPanel>
          </Tabs>
        </div>
      )}
    </div>
  );
}
